import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PersonalIdValidator } from '@shared/components/personal-id/personal-id-validator';
import { validateDate, validateDOBbeforeDOD } from '@shared/helpers/dateValidator';
import { of, switchMap, timer } from 'rxjs';

export interface DecedentFormConfig {
  includeZipCode?: boolean;
}

const DEBOUNCE_TIME_MS = 1000;
const VALIDATE_NAME_REGEX = /^[a-z ,.'-]+$/i;
const VALIDATE_ZIP_CODE_REGEX = /^\d{5}$/;

@Injectable({ providedIn: 'root' })
export class DecedentFormBuilderService {
  constructor(private fb: UntypedFormBuilder) {}

  buildForm(config: DecedentFormConfig = {}): UntypedFormGroup {
    const form = this.fb.group({
      firstName: [
        '',
        [Validators.required, Validators.pattern(VALIDATE_NAME_REGEX), Validators.maxLength(35)],
      ],
      middleName: ['', [Validators.pattern(VALIDATE_NAME_REGEX), Validators.maxLength(1)]],
      lastName: [
        '',
        [Validators.required, Validators.pattern(VALIDATE_NAME_REGEX), Validators.maxLength(35)],
      ],
      decedentId_private: ['', [Validators.required]],
      dateOfBirth: ['', [Validators.required]],
      dateOfDeath: ['', [Validators.required]],
      isDOBbeforeDOD: ['invalid', [validateDOBbeforeDODflag]],
    });

    form.get('decedentId_private')!.setAsyncValidators(asyncValidateDecedentId);
    form.get('dateOfBirth')!.setAsyncValidators(createAsyncDOBValidator(form));
    form.get('dateOfDeath')!.setAsyncValidators(createAsyncDODValidator(form));

    if (config.includeZipCode) {
      form.addControl(
        'zipCode',
        this.fb.control('', {
          validators: [
            Validators.required,
            Validators.pattern(VALIDATE_ZIP_CODE_REGEX),
            Validators.minLength(5),
          ],
          updateOn: 'blur',
        }),
      );
    }

    return form;
  }
}

export function validateDOBbeforeDODflag(control: AbstractControl) {
  return control.value === 'valid' ? null : { dodBeforeDobError: 'DOB MUST BE BEFORE DOD' };
}

export function updateDOBbeforeDOD(
  form: UntypedFormGroup,
  dobValue: string,
  dodValue: string,
): void {
  const valid = validateDOBbeforeDOD(dobValue, dodValue);
  if (valid != null) {
    form.get('isDOBbeforeDOD')!.setValue(valid ? 'valid' : 'invalid', { emitEvent: false });
    form.get('isDOBbeforeDOD')!.markAsDirty();
  }
}

export function createAsyncDOBValidator(form: UntypedFormGroup) {
  return (control: AbstractControl) => {
    return timer(DEBOUNCE_TIME_MS).pipe(
      switchMap(() => {
        updateDOBbeforeDOD(form, control.value, form.get('dateOfDeath')!.value);
        return of(validateDate(control.value));
      }),
    );
  };
}

export function createAsyncDODValidator(form: UntypedFormGroup) {
  return (control: AbstractControl) => {
    return timer(DEBOUNCE_TIME_MS).pipe(
      switchMap(() => {
        updateDOBbeforeDOD(form, form.get('dateOfBirth')!.value, control.value);
        return of(validateDate(control.value));
      }),
    );
  };
}

export function asyncValidateDecedentId(control: AbstractControl) {
  return timer(DEBOUNCE_TIME_MS).pipe(switchMap(() => of(PersonalIdValidator(control))));
}
